import React from 'react';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';

import { RANGE_PRESET_LABELS, isRangeDay } from '../../utils/chartRanges';
import BuilderStep from './BuilderStep';
import { MULTI_WORDS, rangeOptions } from './builderModel';
import { Field, SegmentedField } from './controls';
import { messageProps } from './FieldMessage';

/** The date select's value for a chart with no date. */
export const NO_DATE = '__none';
const ALL_TIME = '__all';
const CUSTOM = '__custom';

const rangeValue = (range) => {
	if (!range) return ALL_TIME;
	if (range.preset) return range.preset;
	return CUSTOM;
};

const MIN_DAY = dayjs('1900-01-01');
const MAX_DAY = dayjs('2200-12-31');

/**
 * Step 6, Time: the date the chart counts by — Created, Updated, or one of
 * the card's date elements — and the range of it, in the collection's own
 * days (ruled q06; the server works the range out in the collection's zone).
 * An element holding several dates says how to read it (ruled d06), with one
 * line on what that does to the chart's links. The comparison with the
 * previous period is one of the form's options (Show as).
 */
export default function TimeStep({ draft, view, onTime, messagesFor, step }) {
	const time = draft.time;
	const options = [{ value: NO_DATE, label: 'No date' }, ...view.time.options];
	const rangeOpts = [
		{ value: ALL_TIME, label: RANGE_PRESET_LABELS.all },
		...rangeOptions().map((preset) => ({ value: preset, label: RANGE_PRESET_LABELS[preset] })),
		{ value: CUSTOM, label: 'Custom dates…' },
	];
	const onMessages = messagesFor('time.on');
	const rangeMessages = messagesFor('time.range');
	const pickRange = (value) => {
		if (value === ALL_TIME) onTime({ range: null });
		else if (value === CUSTOM) {
			// Custom starts from the last 30 calendar days, so no half-chosen range is ever sent.
			const to = dayjs();
			onTime({ range: { from: to.subtract(29, 'day').format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') } });
		} else onTime({ range: { preset: value } });
	};
	return (
		<BuilderStep step='time' number={6} title='Time' {...step}>
			<Field
				id='chart-builder-time-on'
				label='Date'
				messages={onMessages}
				hint={time.on ? "Days are the collection's, in its time zone." : 'Without a date the chart counts every item, whenever it was added.'}
			>
				<Select
					id='chart-builder-time-on'
					virtual={false}
					className='chart-builder__select'
					value={time.on || NO_DATE}
					options={options}
					onChange={(value) => onTime(value === NO_DATE ? { on: null, range: null, compare: false, multi: null } : { on: value })}
					popupMatchSelectWidth={false}
					{...messageProps(onMessages, 'chart-builder-time-on-messages')}
				/>
			</Field>
			{time.on && view.time.multi ? (
				<SegmentedField
					id='chart-builder-time-multi'
					label={`${view.time.field ? view.time.field.label : 'This date'} can hold several dates`}
					value={time.multi || 'earliest'}
					options={view.time.multi.map((reading) => ({ label: MULTI_WORDS[reading] || reading, value: reading }))}
					onChange={(multi) => onTime({ multi })}
					messages={messagesFor('time.multi')}
					hint={view.time.door ? view.time.door.text : null}
				/>
			) : null}
			{time.on ? (
				<Field id='chart-builder-time-range' label='Range' messages={rangeMessages}>
					<Select
						id='chart-builder-time-range'
						virtual={false}
						className='chart-builder__select'
						value={rangeValue(time.range)}
						options={rangeOpts}
						onChange={pickRange}
						popupMatchSelectWidth={false}
						{...messageProps(rangeMessages, 'chart-builder-time-range-messages')}
					/>
					{time.range && time.range.from ? (
						<DatePicker.RangePicker
							className='chart-builder__dates'
							aria-label='Custom dates'
							value={[dayjs(time.range.from), dayjs(time.range.to)]}
							allowClear={false}
							disabledDate={(day) => day.isBefore(MIN_DAY, 'day') || day.isAfter(MAX_DAY, 'day')}
							onChange={(next) => {
								const from = next && next[0] ? next[0].format('YYYY-MM-DD') : null;
								const to = next && next[1] ? next[1].format('YYYY-MM-DD') : null;
								if (isRangeDay(from) && isRangeDay(to) && from <= to) onTime({ range: { from, to } });
							}}
						/>
					) : null}
				</Field>
			) : null}
		</BuilderStep>
	);
}
